<template>
  <li class="member-item d-flex justify-content-between">
    <div class="d-flex align-items-center mw-0">
      <user-short :user-id="member.user.id" length="30" />
      <div class="mw-0">
        <div class="px-2 text-truncate" v-no-capture>{{ member.user.displayName }}</div>
        <div class="px-2 text-truncate text-prime text-small-1" v-no-capture>
          {{ member.user.email }}
        </div>
      </div>
    </div>
    <div class="d-flex align-items-center">
      <div class="text-left role-col">
        <span v-if="member.type === 'owner'">Owner</span>
        <span v-else>Member</span>
      </div>
      <b-dropdown
        variant="default"
        class="team-settings-dropdown"
        menu-class="menu-shadow privilege-dropdown"
        toggle-class="border-0 privilege-dropdown-toggle flex-center"
        toggle-tag="span"
      >
        <template slot="button-content">
          <div>
            <i :class="member.type === 'owner' ? 'owner-icon' : 'user-icon'"></i>
          </div>
        </template>
        <b-dropdown-item
          class="settings-dropdown-item"
          @click="changeUserPrivilege(member, 'owner')"
        >
          <div class="d-flex mx-n2">
            <i class="owner-icon mr-2 flex-shrink-0"></i>
            <div>
              <div class="text-small" :class="{ bold: member.type === 'owner' }">
                {{ memberType }} Owner
              </div>
              <div class="text-small text-secondary text-wrap">
                Responsible for all goals in the
                {{ memberType }}. Can add new members
              </div>
            </div>
          </div>
        </b-dropdown-item>
        <b-dropdown-item
          class="settings-dropdown-item"
          @click="changeUserPrivilege(member, 'regular')"
        >
          <div class="d-flex mx-n2">
            <i class="user-icon mr-2 flex-shrink-0"></i>
            <div>
              <div class="text-small" :class="{ bold: member.type === 'regular' }">
                {{ memberType }} member
              </div>
              <div class="text-small text-secondary text-wrap">
                Contribute to {{ memberType }} goals
              </div>
            </div>
          </div>
        </b-dropdown-item>
      </b-dropdown>
      <b-button
        :id="'remove-' + member.userId"
        class="text-decoration-none grey-bg remove-member flex-center ml-2"
        size="sm"
        variant="link"
        @click="onRemoveClick(member)"
        pill
      >
        <i class="close-icon"></i>
      </b-button>
      <b-tooltip
        :delay="{ show: 500, hide: 0 }"
        :target="'remove-' + member.userId"
        placement="bottom"
        triggers="hover"
      >
        Remove
      </b-tooltip>
    </div>
    <confirm-dialog
      v-model="showRemoveConfirmation"
      @ok="removeMember"
      ok-title="Yes, remove this member"
      ok-variant="danger"
      :hide-header="true"
    >
      <div v-if="team.parentId">
        User will still be part of the organization - <b>{{ parentTeam.name }}</b>
      </div>
      Are you sure you want to remove this member from <b class="text-nowrap">{{ team.name }}</b
      >?
    </confirm-dialog>
  </li>
</template>

<script>
import TeamMixin from '@/mixins/TeamMixin'
import { BTooltip, BButton, BDropdown, BDropdownItem } from 'bootstrap-vue'
import ConfirmDialog from '@/components/ConfirmDialog'
import UserShort from '@/components/UserShort'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component renders a row for org/team member. Row has below capabilites in it
 * - Change role(owner/non-owner) of the member
 * - Remove member from team/org
 *
 * Props:
 * - member
 * - orgId
 * - teamId : it is reqd only when member belongs to a team
 *
 * @emits
 * - update: emitted when role is changes. member object is passed as payload
 * - remove: emitted when member is removed from org/team
 */
export default {
  name: 'MemberRow',
  props: {
    member: Object,
    orgId: [Number, String],
    teamId: [String, Number]
  },
  mixins: [TeamMixin],
  components: {
    UserShort,
    BTooltip,
    BButton,
    BDropdown,
    BDropdownItem,
    ConfirmDialog
  },
  data() {
    return {
      memberSelectedForRemoval: null,
      showRemoveConfirmation: false
    }
  },
  computed: {
    memberType() {
      return this.isOrgLikeTeam ? 'Org' : 'Team'
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    isOrgLikeTeam() {
      return this.team.isOrgLike
    },
    parentTeam() {
      const teamId = this.getClosestOrgLikeTeamId(this.teamId)
      return this.$store.getters.teamById(teamId)
    }
  },
  methods: {
    ...mapActions(['updateOrgMember', 'updateTeamMember', 'removeTeamMember']),
    onRemoveClick(member) {
      this.memberSelectedForRemoval = member
      this.showRemoveConfirmation = true
    },
    async changeUserPrivilege(member, type) {
      const updater = this.teamId ? this.updateTeamMember : this.updateOrgMember
      const updateTeamMember = updater.bind(this, {
        userId: member.user.id,
        type,
        orgId: this.orgId,
        teamId: this.teamId
      })
      const response = await this.wrapOverError(updateTeamMember)
      if (response) {
        EventBus.$emit('ADD_TOAST', { message: 'Changes saved successfully' })
        this.$emit('update', { ...this.member, type })
      }
    },
    async wrapOverError(fn) {
      try {
        return await fn()
      } catch (e) {
        let errors = R.pathOr([DEFAULT_ERROR], ['response', 'data', 'detail'], e)
        if (typeof errors === 'string') {
          errors = [errors]
        }
        this.emitErrors(errors)
      }
    },
    emitErrors(errors) {
      errors.forEach(error => {
        EventBus.$emit('ADD_TOAST', {
          message: error,
          type: TOAST_TYPE.ERROR
        })
      })
    },
    async removeMember() {
      const removeTeamMember = this.removeTeamMember.bind(this, {
        orgId: this.orgId,
        userId: this.memberSelectedForRemoval.userId,
        teamId: this.teamId
      })
      const response = await this.wrapOverError(removeTeamMember)
      if (response) {
        this.$emit('remove')
        EventBus.$emit('ADD_TOAST', { message: 'User has been removed successfully' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

/deep/ .privilege-dropdown {
  width: 270px;
  padding: 1rem 0;
}

/deep/ .privilege-dropdown-toggle {
  background-color: $quinary;
  border-radius: 15px;
  height: 28px;
}

/deep/ .remove-member {
  height: 22px;
  width: 22px;
  padding: 0;
}

.member-item {
  margin: 0;
  border-bottom: 1px solid $quinary;
  padding: 8px 0;
}

.grey-bg {
  background-color: $quinary;
}

.role-col {
  min-width: 90px;
}
</style>
